import { useState } from 'react';
import { StatusBanner } from '../../components/ui';
import { inputClassName, labelClassName } from '../ui/classes';
import AuthShell from './AuthShell';
import { saveSpApiCredentials } from './authApi';
import SpApiSellerIdField from './SpApiSellerIdField';

const initialCredentials = {
  clientId: '',
  clientSecret: '',
  refreshToken: '',
  sellerId: '',
  mode: 'production',
};

function SecretField({ label, name, value, onChange, placeholder, visible, onToggle }) {
  return (
    <label className="grid gap-1.5">
      <span className={`${labelClassName} text-[13px]`}>{label}</span>
      <div className="relative">
        <input
          className={`${inputClassName} min-h-11 rounded-xl pr-14`}
          name={name}
          type={visible ? 'text' : 'password'}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required
        />
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 transition hover:text-slate-800"
          onClick={onToggle}
          aria-label={visible ? 'Hide value' : 'Show value'}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </label>
  );
}

function SpApiSetup({ token, onComplete, onSignOut }) {
  const [step, setStep] = useState('form');
  const [form, setForm] = useState(initialCredentials);
  const [savedUser, setSavedUser] = useState(null);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showRefreshToken, setShowRefreshToken] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = await saveSpApiCredentials(token, {
        ...form,
        mode: 'production',
      });
      setSavedUser(payload.user);
      setStep('notice');
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to save SP-API credentials.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const expiryLabel = savedUser?.spApiExpiresAt
    ? new Date(savedUser.spApiExpiresAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '30 days after your next login';

  return (
    <AuthShell
      brandEyebrow="PandaSeller"
      brandTitle="Link Amazon to unlock the workspace."
      brandBody="Paste production SP-API credentials. We verify live with Amazon, then encrypt and store them."
    >
      {step === 'form' ? (
        <form className="grid gap-5" onSubmit={handleSubmit} noValidate>
          <header>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700">
                Amazon SP-API
              </p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Production
              </span>
            </div>
            <h2 className="mt-2 text-[1.7rem] font-semibold tracking-tight text-slate-900">
              Connect credentials
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Client ID, secret, refresh token, and Seller ID from Seller Central.
            </p>
          </header>

          <div className="grid gap-3.5">
            <label className="grid gap-1.5">
              <span className={`${labelClassName} text-[13px]`}>Client ID</span>
              <input
                className={`${inputClassName} min-h-11 rounded-xl`}
                name="clientId"
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="amzn1.application-oa2-client.xxxxxxxxxxxxxxxx"
                value={form.clientId}
                onChange={handleChange}
                required
              />
            </label>

            <SecretField
              label="Client Secret"
              name="clientSecret"
              value={form.clientSecret}
              onChange={handleChange}
              placeholder="amzn1.oa2-cs.v1.xxxxxxxxxxxxxxxx"
              visible={showClientSecret}
              onToggle={() => setShowClientSecret((v) => !v)}
            />

            <SecretField
              label="Refresh Token"
              name="refreshToken"
              value={form.refreshToken}
              onChange={handleChange}
              placeholder="Atzr|xxxxxxxxxxxxxxxx"
              visible={showRefreshToken}
              onToggle={() => setShowRefreshToken((v) => !v)}
            />

            <SpApiSellerIdField value={form.sellerId} onChange={handleChange} />
          </div>

          <StatusBanner status={status} setStatus={setStatus} />

          <div className="grid gap-2.5">
            <button
              type="submit"
              className="crm-btn-primary min-h-11 w-full rounded-xl text-[15px] font-semibold shadow-[0_14px_30px_-14px_rgb(37_99_235_/_0.8)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Verifying with Amazon…
                </span>
              ) : (
                'Verify & save'
              )}
            </button>
            <button
              type="button"
              className="min-h-11 w-full rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              onClick={onSignOut}
              disabled={isSubmitting}
            >
              Sign out
            </button>
          </div>
        </form>
      ) : (
        <div className="grid gap-5">
          <header>
            <div className="inline-grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="mt-4 text-[1.7rem] font-semibold tracking-tight text-slate-900">
              You’re connected
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Production SP-API is linked. You can open the dashboard now.
            </p>
          </header>

          <dl className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Environment</dt>
              <dd className="font-semibold text-slate-900">Production</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Expires</dt>
              <dd className="font-semibold text-slate-900">{expiryLabel}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500">Seller ID</dt>
              <dd className="font-mono text-[13px] font-semibold text-slate-900">
                {savedUser?.spApiSellerId || form.sellerId || '—'}
              </dd>
            </div>
          </dl>

          <button
            type="button"
            className="crm-btn-primary min-h-11 w-full rounded-xl text-[15px] font-semibold shadow-[0_14px_30px_-14px_rgb(37_99_235_/_0.8)]"
            onClick={() => onComplete(savedUser)}
          >
            Continue to dashboard
          </button>
        </div>
      )}
    </AuthShell>
  );
}

export default SpApiSetup;
